import React, { useState } from "react";
import {
  Save,
  User,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Loader2,
  Ban,
  Shield,
  ThumbsUp,
  ThumbsDown,
  ClipboardList,
} from "lucide-react";
import { tasksApi } from "../../services/api";
import { notifyError } from "../ui/Toast";
import SearchableSelect from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";

interface Task {
  _id: string;
  title: string;
  description: string | null;
  status: "todo" | "doing" | "blocked" | "done";
  priority: "low" | "normal" | "high" | "urgent";
  dueAt: string | null;
  startAt?: string | null;
  ownerUserId: { _id: string; name: string; email: string } | null;
  assigneeUserIds: { _id: string; name: string }[];
  approvals: {
    required: boolean;
    status: string;
    by: { _id: string; name: string } | null;
    at: string | null;
    note: string | null;
  }[];
  completedAt: string | null;
  blockedReason?: string | null;
}

interface TaskDetailModalProps {
  task: Task;
  onClose: () => void;
  onUpdate: () => void;
}

const statusConfig: Record<string, { label: string; Icon: React.FC<any>; color: string }> = {
  todo: { label: "To Do", Icon: Circle, color: "text-slate-500" },
  doing: { label: "Doing", Icon: Loader2, color: "text-blue-500" },
  blocked: { label: "Blocked", Icon: Ban, color: "text-red-500" },
  done: { label: "Done", Icon: CheckCircle2, color: "text-emerald-500" },
};

const priorityConfig: Record<string, { label: string; className: string }> = {
  low: { label: "Low", className: "bg-slate-100 text-slate-600" },
  normal: { label: "Normal", className: "bg-blue-100 text-blue-700" },
  high: { label: "High", className: "bg-orange-100 text-orange-700" },
  urgent: { label: "Urgent", className: "bg-red-100 text-red-700" },
};

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ task, onClose, onUpdate }) => {
  const [form, setForm] = useState({
    title: task.title,
    description: task.description || "",
    priority: task.priority,
    dueAt: task.dueAt ? task.dueAt.slice(0, 10) : "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await tasksApi.update(task._id, {
        title: form.title,
        description: form.description || null,
        priority: form.priority,
        dueAt: form.dueAt || null,
      });
      onUpdate();
    } catch (err) {
      console.error("Failed to update task:", err);
      notifyError("Couldn't save your changes to the task.", err);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      await tasksApi.changeStatus(task._id, newStatus);
      onUpdate();
    } catch (err) {
      console.error("Failed to change status:", err);
      notifyError("Couldn't change the task status.", err);
    }
  };

  const handleApproval = async (index: number, status: "approved" | "rejected") => {
    try {
      await tasksApi.updateApproval(task._id, index, { status });
      onUpdate();
    } catch (err) {
      console.error("Failed to update approval:", err);
      notifyError("Couldn't update the approval.", err);
    }
  };

  const sc = statusConfig[task.status] || statusConfig.todo;
  const StatusIcon = sc.Icon;
  const pc = priorityConfig[task.priority] || priorityConfig.normal;

  return (
    <PremiumModal
      open={true}
      onClose={onClose}
      title="Task Details"
      icon={ClipboardList}
      size="lg"
    >
      <div className="space-y-6">
          {/* Title & Description */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
                placeholder="Add task notes or description..."
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                <SearchableSelect
                  options={[
                    { value: "low", label: "Low" },
                    { value: "normal", label: "Normal" },
                    { value: "high", label: "High" },
                    { value: "urgent", label: "Urgent" },
                  ]}
                  value={form.priority}
                  onChange={(val) => setForm({ ...form, priority: (val || "normal") as any })}
                  placeholder="Select priority"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={form.dueAt}
                  onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: "var(--color-primary)" }}
              >
                <Save className="h-4 w-4" />
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

          {/* Status & Priority Row */}
          <div className="flex flex-wrap gap-6">
            <div>
              <p className="text-xs text-slate-400 uppercase mb-1.5">Status</p>
              <div className="flex items-center gap-2">
                <StatusIcon className={`h-4 w-4 ${sc.color}`} />
                <span className="text-sm font-medium text-slate-700">{sc.label}</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase mb-1.5">Priority</p>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${pc.className}`}>
                {task.priority === "urgent" && (
                  <AlertTriangle className="inline h-3 w-3 mr-0.5 -mt-0.5" />
                )}
                {pc.label}
              </span>
            </div>
            {task.dueAt && (
              <div>
                <p className="text-xs text-slate-400 uppercase mb-1.5">Due Date</p>
                <div className="flex items-center gap-1 text-sm text-slate-700">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  {new Date(task.dueAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Owner / Assignees */}
          <div className="space-y-3">
            <p className="text-xs text-slate-400 uppercase">Team</p>
            {task.ownerUserId && typeof task.ownerUserId === "object" && (
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                  style={{ backgroundColor: "var(--color-accent)" }}
                >
                  {task.ownerUserId.name
                    ?.split(" ")
                    .slice(0, 2)
                    .map((w) => w[0]?.toUpperCase())
                    .join("")}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">{task.ownerUserId.name}</p>
                  <p className="text-xs text-slate-500">Owner</p>
                </div>
              </div>
            )}
            {task.assigneeUserIds?.map((u) => (
              <div key={u._id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="h-8 w-8 rounded-full flex items-center justify-center bg-slate-200 text-slate-600 text-xs font-bold">
                  {u.name
                    ?.split(" ")
                    .slice(0, 2)
                    .map((w) => w[0]?.toUpperCase())
                    .join("")}
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">{u.name}</p>
                  <p className="text-xs text-slate-500">Assignee</p>
                </div>
              </div>
            ))}
          </div>

          {/* Approvals */}
          {task.approvals && task.approvals.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs text-slate-400 uppercase flex items-center gap-1">
                <Shield className="h-3.5 w-3.5" />
                Approvals
              </p>
              {task.approvals.map((a, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      Approval {i + 1}
                      {a.required && <span className="text-red-500 ml-1">*</span>}
                    </p>
                    <p className="text-xs text-slate-500">
                      Status:{" "}
                      <span
                        className={`font-medium ${
                          a.status === "approved"
                            ? "text-emerald-600"
                            : a.status === "rejected"
                              ? "text-red-600"
                              : "text-amber-600"
                        }`}
                      >
                        {a.status.charAt(0).toUpperCase() + a.status.slice(1)}
                      </span>
                    </p>
                    {a.note && <p className="text-xs text-slate-500 mt-1">{a.note}</p>}
                  </div>
                  {a.status === "pending" && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApproval(i, "approved")}
                        className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                        title="Approve"
                      >
                        <ThumbsUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleApproval(i, "rejected")}
                        className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                        title="Reject"
                      >
                        <ThumbsDown className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Blocked Reason */}
          {task.status === "blocked" && task.blockedReason && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs text-red-600 uppercase font-medium mb-1">Blocked Reason</p>
              <p className="text-sm text-red-700">{task.blockedReason}</p>
            </div>
          )}

          {/* Status Change Actions */}
          <div className="border-t border-slate-200 pt-4">
            <p className="text-xs text-slate-400 uppercase mb-3">Change Status</p>
            <div className="flex flex-wrap gap-2">
              {["todo", "doing", "blocked", "done"]
                .filter((s) => s !== task.status)
                .map((s) => {
                  const cfg = statusConfig[s];
                  const SIcon = cfg.Icon;
                  return (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(s)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      <SIcon className={`h-4 w-4 ${cfg.color}`} />
                      {cfg.label}
                    </button>
                  );
                })}
            </div>
          </div>
      </div>
    </PremiumModal>
  );
};

export default TaskDetailModal;
